import axios from 'axios';
import store from '@/store';

/**
 * Service centralisé pour gérer les fonctionnalités principales de l'application
 * - Gère la synchronisation des données
 * - Centralise les appels aux APIs core
 * - Traite les données pour les algorithmes de recommandation
 */
class AppService {
  /**
   * Initialise le service app
   */
  constructor() {
    this.syncInterval = null;
    this.lastSyncTime = localStorage.getItem('lastSyncTime') || null;
  }

  /**
   * Initialise le service d'application
   * À appeler lors du démarrage de l'application
   */
  init() {
    // Charger les préférences utilisateur depuis localStorage
    this.loadUserPreferences();
    
    // Configurer la synchronisation
    this.setupSync();
    
    // Vérifier les mises à jour des données
    this.checkForUpdates();
    
    console.log('App Service initialized');
  }

  /**
   * Charge les préférences utilisateur depuis localStorage
   */
  loadUserPreferences() {
    try {
      // Préférences de thème
      const darkMode = localStorage.getItem('darkMode') === 'true';
      store.commit('ui/SET_DARK_MODE', darkMode);
      
      // Préférences de catégories
      const preferredCategories = JSON.parse(localStorage.getItem('preferredCategories') || '[]');
      store.commit('ui/SET_PREFERRED_CATEGORIES', preferredCategories);
      
      // Sources suivies
      const followedSources = JSON.parse(localStorage.getItem('followedSources') || '[]');
      store.commit('ui/SET_FOLLOWED_SOURCES', followedSources);
      
      // Historique de lectures
      const readingHistory = JSON.parse(localStorage.getItem('readingHistory') || '[]');
      store.commit('ui/SET_READING_HISTORY', readingHistory);
      
      console.log('User preferences loaded from localStorage');
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  }

  /**
   * Configure la synchronisation périodique des données
   */
  setupSync() {
    // Si déjà configuré, nettoyer l'intervalle existant
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    
    // Synchroniser toutes les 15 minutes
    this.syncInterval = setInterval(() => {
      this.syncData();
    }, 15 * 60 * 1000);
  }

  /**
   * Synchronise les données de l'utilisateur avec le backend
   */
  async syncData() {
    // Vérifier si l'utilisateur est connecté
    if (!store.getters['auth/isAuthenticated']) {
      console.log('User not authenticated, skipping data sync');
      return;
    }
    
    try {
      // 1. Récupérer les données à synchroniser
      const dataToSync = {
        readingHistory: JSON.parse(localStorage.getItem('readingHistory') || '[]'),
        preferences: {
          categories: JSON.parse(localStorage.getItem('preferredCategories') || '[]'),
          sources: JSON.parse(localStorage.getItem('followedSources') || '[]')
        },
        lastSyncTime: this.lastSyncTime
      };
      
      // 2. Envoyer les données au backend
      const response = await axios.post('/api/users/sync', dataToSync);
      
      // 3. Mettre à jour les données locales avec les données provenant du backend
      if (response.data.success) {
        // Mettre à jour le timestamp de dernière synchronisation
        this.lastSyncTime = new Date().toISOString();
        localStorage.setItem('lastSyncTime', this.lastSyncTime);
        
        console.log('Data synchronized successfully');
      }
    } catch (error) {
      console.error('Error syncing data:', error);
    }
  }

  /**
   * Vérifie les mises à jour des données
   */
  async checkForUpdates() {
    try {
      const response = await axios.get('/api/app/updates');
      // Assurez-vous que le chemin est correct et qu'il n'y a pas de double /api
      
      if (response.data.hasUpdates) {
        // Traiter les mises à jour
        console.log('Updates available:', response.data.updates);
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }

  /**
   * Enregistre un article vu dans l'historique de lecture
   * @param {Object} article - L'article à enregistrer
   */
  recordArticleView(article) {
    try {
      // 1. Charger l'historique existant
      const readingHistory = JSON.parse(localStorage.getItem('readingHistory') || '[]');
      
      // 2. Vérifier si l'article est déjà dans l'historique
      const existingIndex = readingHistory.findIndex(item => item.id === article._id);
      
      // 3. Préparation des données d'historique
      const historyItem = {
        id: article._id,
        title: article.title,
        category: article.category,
        sourceId: article.sourceId?._id || article.sourceId,
        sourceName: article.sourceName,
        timestamp: new Date().toISOString()
      };
      
      // 4. Mise à jour de l'historique
      if (existingIndex !== -1) {
        // Mettre à jour l'entrée existante
        readingHistory[existingIndex] = historyItem;
      } else {
        // Ajouter une nouvelle entrée
        readingHistory.unshift(historyItem);
      }
      
      // 5. Limiter la taille de l'historique (garder les 100 derniers articles)
      const trimmedHistory = readingHistory.slice(0, 100);
      
      // 6. Sauvegarder l'historique mis à jour
      localStorage.setItem('readingHistory', JSON.stringify(trimmedHistory));
      
      // 7. Mettre à jour le store
      store.commit('ui/SET_READING_HISTORY', trimmedHistory);
      
      // 8. Si l'utilisateur est connecté, envoyer les données au backend
      if (store.getters['auth/isAuthenticated']) {
        this.sendArticleViewToBackend(article._id);
      }
      
      console.log('Article view recorded:', article.title);
    } catch (error) {
      console.error('Error recording article view:', error);
    }
  }

  /**
   * Envoie la vue d'article au backend pour le suivi et les recommandations
   * @param {string} articleId - ID de l'article vu
   */
  async sendArticleViewToBackend(articleId) {
    try {
      await axios.post('/api/articles/view', { articleId });
    } catch (error) {
      console.error('Error sending article view to backend:', error);
    }
  }

  /**
   * Met à jour les préférences de catégories de l'utilisateur
   * @param {Array} categories - Tableau des catégories préférées
   */
  updatePreferredCategories(categories) {
    try {
      // Enregistrer localement
      localStorage.setItem('preferredCategories', JSON.stringify(categories));
      store.commit('ui/SET_PREFERRED_CATEGORIES', categories);
      
      // Si l'utilisateur est connecté, synchroniser avec le backend
      if (store.getters['auth/isAuthenticated']) {
        this.syncData();
      }
    } catch (error) {
      console.error('Error updating preferred categories:', error);
    }
  }

  /**
   * Met à jour les sources suivies par l'utilisateur
   * @param {Array} sources - Tableau des IDs de sources suivies
   */
  updateFollowedSources(sources) {
    try {
      // Enregistrer localement
      localStorage.setItem('followedSources', JSON.stringify(sources));
      store.commit('ui/SET_FOLLOWED_SOURCES', sources);
      
      // Si l'utilisateur est connecté, synchroniser avec le backend
      if (store.getters['auth/isAuthenticated']) {
        this.syncData();
      }
    } catch (error) {
      console.error('Error updating followed sources:', error);
    }
  }
}

// Créer une instance unique du service à utiliser dans toute l'application
const appService = new AppService();

export default appService;